// libs
import React, {
    memo,
    forwardRef,
    useImperativeHandle,
    useState,
    RefForwardingComponent,
    useEffect,
    useRef,
} from "react";
import isEmpty from "lodash/isEmpty";

// styles
import { SearchContainer } from "./styles";

// defs
interface ISearchProps {
    onChange?: (text: string) => void;
    onSubmit?: (text: string) => void;
    showIcon?: boolean;
    placeHolder?: string;
    autoFocus?: boolean;
    className?: string;
    initialValue?: string;
}

export interface ISearchInputRef {
    setValue: (value: string) => void;
}

const EMPTY_FUNC = () => {
    /** DO NOTHING */
};

const SEARCH = "Search";

const SearchInput: RefForwardingComponent<ISearchInputRef, ISearchProps> = (
    {
        onChange = EMPTY_FUNC,
        onSubmit = EMPTY_FUNC,
        placeHolder = "",
        showIcon = true,
        autoFocus = false,
        className = "",
        initialValue = "",
    }: ISearchProps,
    ref
) => {
    const [inputValue, updateInputValue] = useState<string>(initialValue);
    const inpRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        autoFocus && inpRef.current !== null && inpRef.current.focus();
    }, [autoFocus]);

    const setInputValue = (value: string) => {
        updateInputValue(value);
        onChange(value);
    };

    useImperativeHandle(ref, () => ({
        setValue: (value: string) => setInputValue(value),
    }));

    const onInputChange: React.ChangeEventHandler<HTMLInputElement> = event =>
        setInputValue(event.target.value);

    const onInputKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key.toUpperCase() === "ENTER" && !isEmpty(inputValue)) {
            onSubmit(inputValue);
        }
    };

    return (
        <SearchContainer showIcon={showIcon} className={className}>
            {showIcon && <i className="icon icon-magnify" />}
            <input
                type="text"
                ref={inpRef}
                aria-label={SEARCH}
                onChange={onInputChange}
                value={inputValue}
                onKeyPress={onInputKeyPress}
                placeholder={placeHolder}
                className="input"
            />
        </SearchContainer>
    );
};

export default memo(forwardRef(SearchInput));
